import React from 'react';
import { RadioButtonGroup } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useControlDisabled } from './ControlDisabledContext';
import { joinClasses } from './fieldClassSlots';

export interface OASISRadioRenderProps {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  label: string;
}

export interface OASISRadioProps {
  qid: string;
  /** Outer `[data-qid]` wrapper — grid spans, margins */
  className?: string;
  /**
   * Inner wrapper around prompt + radio cluster. Omit for defaults (`space-y-2` or inline flex strip).
   * When set, replaces those defaults entirely.
   */
  innerClassName?: string;
  /** Classes appended to prompt label/spans */
  labelClassName?: string;
  /** RadioButtonGroup root — row/column spacing, wrapping (`gap-*`, `flex-wrap`, …) */
  groupClassName?: string;
  /** Pass-through to RadioButtonGroup (`horizontal` lays options left-to-right) */
  orientation?: 'horizontal' | 'vertical';
  hideLabel?: boolean;
  /**
   * Prompt and option cluster on one row (within `innerClassName` or default strip).
   * Useful with `orientation: "horizontal"` for compact Yes / No strips.
   */
  inline?: boolean;
  children?: (props: OASISRadioRenderProps) => React.ReactNode;
}

export function OASISRadio({
  qid,
  className,
  innerClassName,
  labelClassName,
  groupClassName,
  orientation,
  hideLabel,
  inline,
  children,
}: OASISRadioProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const inheritedDisabled = useControlDisabled();
  const question = schemaMap.get(qid);

  const value = (answers[qid] as string) ?? '';
  const onChange = (v: string) => onAnswerChange(qid, v);

  const options = (question?.values ?? []).map(val => ({
    value: val,
    label: question?.value_description?.[val]
      ? `${val} - ${question.value_description[val]}`
      : val,
  }));

  const label = question?.question ?? question?.description ?? qid;

  const innerDefault = inline ? 'flex min-w-0 flex-col gap-2' : 'space-y-2';
  const inner = innerClassName != null ? innerClassName : innerDefault;

  const blockLabelCls = joinClasses(
    'block min-w-0 break-words text-sm font-semibold text-foreground',
    labelClassName
  );

  const rg =
    options.length > 0 ? (
      <RadioButtonGroup
        disabled={inheritedDisabled}
        {...(orientation != null ? { orientation } : {})}
        className={groupClassName}
        options={options}
        value={value}
        onValueChange={onChange}
        allowDeselect
      />
    ) : (
      <p className='text-muted-foreground text-xs leading-snug'>
        No choices in loaded schema for{' '}
        <code className='rounded bg-muted px-1 py-0.5 text-[11px] font-mono'>
          {qid}
        </code>
        . Select a RogerDoc schema + section that define this radio in the
        slice.
      </p>
    );

  return (
    <div data-qid={qid} className={joinClasses('min-w-0', className)}>
      {children ? (
        children({ value, onChange, options, label })
      ) : inline ? (
        <div className={inner}>
          {!hideLabel && <label className={blockLabelCls}>{label}</label>}
          <div className='min-w-0'>{rg}</div>
        </div>
      ) : (
        <div className={inner}>
          {!hideLabel && <label className={blockLabelCls}>{label}</label>}
          {rg}
        </div>
      )}
    </div>
  );
}
